// Like Promise.all(items.map(fn)), but with at most `limit` calls in flight.
// The API's nginx refuses more than 20 open connections from one visitor and
// answers the rest with 503, so a bulk action that sends one request per item
// all at once loses some of them. After a failure no new calls start, the ones
// in flight finish, and the first error is thrown.
export async function mapWithLimit<T, R>(
  items: T[],
  fn: (item: T, index: number) => Promise<R>,
  limit = 4
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let next = 0;
  let failed = false;

  async function worker(): Promise<void> {
    while (!failed && next < items.length) {
      const index = next++;
      try {
        results[index] = await fn(items[index], index);
      } catch (error) {
        failed = true;
        throw error;
      }
    }
  }

  const workers = Array.from({ length: Math.min(limit, items.length) }, () =>
    worker()
  );
  await Promise.all(workers);
  return results;
}
